import React from 'react'
import _ from 'lodash/fp'

import { AuthPanel } from '../Auth/Common'

// Material UI things
import withStyles from '@material-ui/core/styles/withStyles'
import Button from '@material-ui/core/Button'
import ProfileForm from '../../components/profile/ProfileForm'

import { removeGhostLoader, upsertGhostLoader } from './buddiesQueries'
import colors from '@fe/ui/colors'
import ConfirmDialog from '@fe/ui/dialogs/ConfirmDialog'

const deletePanelStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  flex: 1,
  width: '100%',
  padding: '10px 10px 0 0'
}

function DeleteButton ({ handleShowDeleteDialog }) {
  return (
    <div style={deletePanelStyle}>
      <Button onClick={handleShowDeleteDialog}>Delete</Button>
    </div>
  )
}

const styles = _theme => ({
  container: {
    position: 'absolute',
    top: 64
  },
  bottomActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-around',
    marginTop: '2em'
  },
  errorText: {
    color: colors.primaryDarkRed
  },
  savingText: {
    color: colors.secondaryGreen
  },
  fieldContainer: {
    maxWidth: 400,
    width: '90%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  }
})

const prepareProfile = _.flow(
  _.pick([
    'id',
    'email',
    'name',
    'handicap',
    'sex',
    'countryCode',
    'birthday',
    'teeLength',
    'clubId',
    'clubName'
  ]),
  _.update('handicap', h => {
    const newHandicap = parseFloat(h)
    return _.isNaN(newHandicap) ? 99 : newHandicap
  }),
  _.update('teeLength', t => {
    const newTeeLength = parseInt(t)
    return _.isNaN(newTeeLength) ? undefined : newTeeLength
  })
)

function UpsertGhostDialog ({
  mode = 'create', // edit | create
  open,
  hide,
  ghostProfile,
  upsertGhost,
  removeGhost,
  classes
}) {
  const [profile, setProfile] = React.useState(ghostProfile || {})
  const [waiting, setWaiting] = React.useState(false)
  const [error, setError] = React.useState(undefined)
  const isValid = React.useMemo(() => (
    !_.isEmpty(profile.name) &&
    !_.isEmpty(profile.sex) &&
    !_.isEmpty(profile.countryCode) &&
    _.isNumber(parseFloat(profile.handicap)) &&
    !_.isNaN(parseFloat(profile.handicap))
  ), [profile])

  const [deleteDialog, setDeleteDialog] = React.useState(false)
  const handleShowDeleteDialog = React.useCallback(() => { setDeleteDialog(true) }, [setDeleteDialog])
  const handleHideDeleteDialog = React.useCallback(() => { setDeleteDialog(false) }, [setDeleteDialog])
  const handleDelete = React.useCallback(async () => {
    await removeGhost({
      variables: {
        id: _.get('id', ghostProfile)
      }
    })
    hide()
  }, [ghostProfile, removeGhost, hide])

  React.useEffect(() => {
    if (_.isEmpty(ghostProfile)) {
      setProfile({})
    } else {
      setProfile(_.flow(
        _.set('clubName', ghostProfile.homeClubName),
        _.set('clubId', ghostProfile.homeClubId),
        _.unset('homeClubName'),
        _.unset('homeClubId'),
        _.unset('__typename'),
        _.unset('handle')
      )(ghostProfile))
    }
  }, [setProfile, ghostProfile])

  const handleCancel = React.useCallback(() => { hide() }, [hide])
  const handleUpsert = React.useCallback(() => {
    // id
    // name*
    // handle
    // email
    // countryCode*
    // birthday
    // sex*
    // handicap*
    // clubId
    // clubName
    // teeLength
    setWaiting(true)
    upsertGhost({ variables: { input: prepareProfile(profile) } }).then(result => {
      setWaiting(false)
      setError(undefined)
      hide()
    }).catch(_error => {
      setWaiting(false)
      setError('There was an error submitting.')
    })
  }, [profile, upsertGhost, hide])

  if (!open) {
    return false
  }

  return (
    <div className={classes.container}>
      <AuthPanel
        routed
        noBackground
        handleLeftBoxClick={hide}
      >
        {(mode === 'edit') && (
          <DeleteButton handleShowDeleteDialog={handleShowDeleteDialog} />
        )}
        {((mode === 'edit' && !_.isEmpty(profile)) || mode === 'create') && (
          <div className={classes.fieldContainer}>
            <ProfileForm
              profile={profile}
              setProfile={setProfile}
            />
          </div>
        )}
        <div className={classes.bottomActions}>
          <Button onClick={handleCancel} variant='contained' color='default'>Cancel</Button>
          <Button onClick={handleUpsert} variant='contained' color='primary' disabled={!isValid}>
            {profile.id ? 'Save' : 'Create'}
          </Button>
        </div>

        {waiting && <p className={classes.savingText}>Waiting on server..</p>}
        {error && <p className={classes.errorText}>{error}</p>}
      </AuthPanel>
      <ConfirmDialog
        open={deleteDialog}
        hide={handleHideDeleteDialog}
        confirm={handleDelete}
        confirmText='Delete'
        title='Delete Local User'
        content='Are you sure you wish to delete this local user?'
      />
    </div>
  )
}

export default _.compose(
  withStyles(styles),
  upsertGhostLoader,
  removeGhostLoader
)(UpsertGhostDialog)
